import { Alert, Spin } from "antd";
import Plot, { Figure } from "react-plotly.js";
import NoData from "../../NewSite/Components/OptimizationConfigurationForm/NoData";
import { formatDateTime, plotlyDateAxis } from "../../../util/formatDate";

export type SchedulePoint = {
  windowStart: Date;
  setpoint: number | null;
};

export type ScheduleEvent = {
  timestamp: Date;
  createdAt?: Date;
};

// One per distinct active schedule in the visible range: when it actually
// became ready (`readyAt`), and whether that came from the schedule's own
// `created_at` or fell back to its intended timestamp.
export type WindowReadyEvent = {
  readyAt: Date;
  hasCreatedAt: boolean;
};

export type ScheduleVsMeasurementChartProps = {
  assetName: string;
  schedulePoints: SchedulePoint[];
  measurementTimestamps: Date[];
  measurementValues: (number | null)[];
  scheduleEvents: ScheduleEvent[];
  windowReadyEvents: WindowReadyEvent[];
  windowStart: Date;
  windowEnd: Date;
  isLoading?: boolean;
  error?: Error | null;
};

const toStepSeries = (points: SchedulePoint[], windowEnd: Date) => {
  const x = points.map((p) => p.windowStart.getTime());
  const y = points.map((p) => p.setpoint);
  const lastWindow = points[points.length - 1];
  if (lastWindow) {
    x.push(windowEnd.getTime());
    y.push(lastWindow.setpoint);
  }
  return { x, y };
};

// Number of points sampled along each vertical schedule-event line so Plotly
// has something to hover near for its full height, not just its endpoints.
const SCHEDULE_EVENT_LINE_SAMPLES = 6;

export const ScheduleVsMeasurementChart: React.FC<
  ScheduleVsMeasurementChartProps
> = ({
  assetName,
  schedulePoints,
  measurementTimestamps,
  measurementValues,
  windowReadyEvents,
  windowStart,
  windowEnd,
  isLoading,
  error,
}) => {
  if (error) {
    return <Alert message="Error" description={error.message} type="error" />;
  }

  if (isLoading) {
    return (
      <div>
        <h3>{assetName}</h3>
        <div className="w-full flex justify-center items-center min-h-72">
          <Spin />
        </div>
      </div>
    );
  }

  const hasSchedule = schedulePoints.some((p) => p.setpoint != null);
  const hasMeasurements = measurementValues.some((v) => v != null);

  if (!hasSchedule && !hasMeasurements) {
    return (
      <div>
        <h3>{assetName}</h3>
        <NoData />
      </div>
    );
  }

  const { x: scheduleX, y: scheduleY } = toStepSeries(
    schedulePoints,
    windowEnd,
  );

  // Plotly draws "lines" mode in array order, so any out-of-order point
  // produces a line that visually loops back across the chart. Sort by
  // timestamp defensively since the backend doesn't guarantee ordering.
  const measurementOrder = measurementTimestamps
    .map((_, i) => i)
    .sort(
      (a, b) =>
        measurementTimestamps[a].getTime() - measurementTimestamps[b].getTime(),
    );
  const measurementX = measurementOrder.map((i) =>
    measurementTimestamps[i].getTime(),
  );
  const measurementY = measurementOrder.map((i) => measurementValues[i]);

  // Rendered as scatter traces on a hidden, paper-height secondary y-axis
  // (rather than layout "shapes") so each line can carry its own hover
  // tooltip — Plotly shapes can't show hover text.
  const eventLineY = Array.from(
    { length: SCHEDULE_EVENT_LINE_SAMPLES },
    (_, i) => i / (SCHEDULE_EVENT_LINE_SAMPLES - 1),
  );

  const windowReadyEventTraces: Figure["data"] = windowReadyEvents.map(
    (event, index) => {
      const tMs = event.readyAt.getTime();
      const label = event.hasCreatedAt
        ? `Ready at ${formatDateTime(event.readyAt)}`
        : `Ready at ${formatDateTime(event.readyAt)} (schedule timestamp, no created_at)`;
      return {
        x: eventLineY.map(() => tMs),
        y: eventLineY,
        yaxis: "y2",
        type: "scatter",
        mode: "lines",
        name: "Schedule finished at",
        legendgroup: "schedule-finished-at",
        line: { color: "#1677ff", width: 1, dash: "dot" },
        hoverinfo: "text",
        text: eventLineY.map(() => label),
        showlegend: index === 0,
      };
    },
  );

  const data: Figure["data"] = [
    {
      x: scheduleX,
      y: scheduleY,
      type: "scatter",
      mode: "lines",
      line: { shape: "hv", color: "#1e8c6f", width: 2, dash: "dash" },
      name: "Schedule (most recent setpoint per 15-min window)",
      connectgaps: false,
    },
    {
      x: measurementX,
      y: measurementY,
      type: "scatter",
      mode: "lines",
      line: { shape: "linear", color: "#ff7f0e", width: 2 },
      name: "Measurement",
      connectgaps: false,
    },
    ...windowReadyEventTraces,
  ];

  const layout: Figure["layout"] = {
    xaxis: {
      ...plotlyDateAxis,
      title: "Time",
      range: [windowStart.getTime(), windowEnd.getTime()],
    },
    yaxis: { title: "Power (kW)" },
    yaxis2: {
      overlaying: "y",
      range: [0, 1],
      visible: false,
      fixedrange: true,
    },
    legend: { orientation: "h" },
    margin: { t: 20, b: 40, l: 60, r: 20 },
  };

  return (
    <div>
      <h3>{assetName}</h3>
      <Plot data={data} layout={layout} className="w-full" />
    </div>
  );
};
